/**
 * Share showcase — port of the template's collection section + the
 * word-by-word ScrollRevealText. Shows the sharing experience: the verse
 * share, the circle invite that works in any group chat, and the join page
 * that catches people without the app.
 */
import { useEffect, useRef, useState } from "react";
import { FadeIn } from "./FadeIn";

/** Template's ScrollRevealText: words ink in as the paragraph crosses the viewport. */
function ScrollRevealText({ text }: { text: string }) {
  const containerRef = useRef<HTMLParagraphElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const startOffset = windowHeight * 0.9;
      const endOffset = windowHeight * 0.1;
      const totalDistance = startOffset - endOffset;
      const currentPosition = startOffset - rect.top;
      setProgress(Math.max(0, Math.min(1, currentPosition / totalDistance)));
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const words = text.split(" ");
  return (
    <p ref={containerRef} className="text-3xl font-semibold leading-snug tracking-tight md:text-4xl lg:text-5xl">
      {words.map((word, index) => {
        const revealed = progress > index / words.length;
        return (
          <span
            key={index}
            className="transition-colors duration-150"
            style={{ color: revealed ? "#171717" : "#d4d4d4" }}
          >
            {word}
            {index < words.length - 1 ? " " : ""}
          </span>
        );
      })}
    </p>
  );
}

const SHARE_CARDS = [
  {
    kicker: "Share the verse",
    title: "Every verse ends with a gift to give",
    body: "Receive your word, then send it on. One tap drops the verse into any conversation, exactly as it found you.",
    visual: (
      <div className="flex h-full items-center justify-center p-8">
        <div className="w-full max-w-[260px] rounded-2xl bg-white p-5 shadow-[0_24px_48px_-16px_rgba(23,23,23,0.18)]">
          <p className="font-scripture text-[15px] leading-relaxed text-neutral-900">
            “We have this hope as an anchor for the soul, firm and secure.”
          </p>
          <p className="mt-2 text-xs font-semibold text-neutral-500">Hebrews 6:19</p>
          <div className="mt-4 rounded-full bg-neutral-900 py-2 text-center text-xs font-medium text-white">
            Share this verse
          </div>
        </div>
      </div>
    ),
  },
  {
    kicker: "Invite your circle",
    title: "One link your whole group chat can use",
    body: "Invites work for everyone, app or not. Drop the link in the youth-group chat, the church bulletin, or a QR on the screen.",
    visual: (
      <div className="flex h-full items-center justify-center p-8">
        <div className="w-full max-w-[280px] space-y-3">
          <div className="ml-auto w-fit max-w-full rounded-2xl rounded-br-md bg-neutral-900 px-4 py-3 text-xs leading-relaxed text-white">
            Walk with me in “Youth Group” on Jesus Entered the Chat. Join us here:
            <span className="mt-1 block text-neutral-400">jesusenteredthechat.com/join…</span>
          </div>
          <div className="w-fit rounded-2xl rounded-bl-md bg-white px-4 py-3 text-xs text-neutral-900 shadow-sm">
            I’m in 🙌
          </div>
          <div className="w-fit rounded-2xl rounded-bl-md bg-white px-4 py-3 text-xs text-neutral-900 shadow-sm">
            Same. Starting tonight.
          </div>
        </div>
      </div>
    ),
  },
  {
    kicker: "They land softly",
    title: "A welcome page, not a dead end",
    body: "Friends without the app reach a quiet page that opens their circle in the browser and saves their place until they join.",
    visual: (
      <div className="flex h-full items-center justify-center p-8">
        <div className="w-full max-w-[260px] rounded-2xl bg-white p-6 text-center shadow-[0_24px_48px_-16px_rgba(23,23,23,0.18)]">
          <p className="text-2xl">🕊️</p>
          <p className="mt-2 text-sm font-semibold text-neutral-900">You’ve been invited to a circle</p>
          <div className="mt-4 rounded-full bg-neutral-900 py-2 text-xs font-medium text-white">Open in the app</div>
          <div className="mt-2 rounded-full border border-neutral-200 py-2 text-xs font-medium text-neutral-900">
            Continue in your browser
          </div>
        </div>
      </div>
    ),
  },
];

export function ShareSection() {
  return (
    <section id="share" className="bg-[#FAFAF7]">
      {/* Mission statement — word-by-word ink reveal */}
      <div className="px-6 py-24 md:px-12 md:py-32 lg:px-20">
        <div className="mx-auto max-w-4xl">
          <ScrollRevealText text="Faith was never meant to be practiced alone. Every verse can be shared, every circle can be joined, and every invitation lands softly, whether or not your people have the app yet." />
        </div>
      </div>

      {/* Share experience cards */}
      <div className="px-6 pb-24 md:px-12 lg:px-20">
        <p className="text-xs uppercase tracking-widest text-neutral-400">Built to be shared</p>
        <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
          {SHARE_CARDS.map((card, i) => (
            <FadeIn key={card.kicker} delay={i * 90} className="group">
              <div className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-100 transition-transform duration-500 group-hover:scale-[1.01]">
                {card.visual}
              </div>
              <div className="py-6">
                <p className="mb-2 text-xs uppercase tracking-widest text-neutral-400">{card.kicker}</p>
                <h3 className="text-xl font-semibold text-neutral-900">{card.title}</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-neutral-500">{card.body}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}
